import React from "react";
import { Loader2 } from "lucide-react";
import { sanitizeAIText } from "../../lib/sanitizeAIText";

/**
 * i18n copy for the "inquiry sent" / "inquiry failed" system cards. Kept
 * inline so the whole panel stays language-aware without a hop through the
 * global i18n dict.
 */
const SYSTEM_COPY = {
  et: {
    sentTitle: "Päring on edastatud kliinikusse",
    sentBody: "Vastame e-postile 24 tunni jooksul tööpäeval. Kiireloomulistes küsimustes helista +372 5566 5451.",
    failedTitle: "Saatmine ebaõnnestus",
    failedBody: "Palun täida vorm ise või helista +372 5566 5451.",
  },
  ru: {
    sentTitle: "Запрос отправлен в клинику",
    sentBody: "Мы ответим в течение 24 часов в рабочий день. По срочным вопросам звоните +372 5566 5451.",
    failedTitle: "Не удалось отправить",
    failedBody: "Пожалуйста, заполните форму или позвоните +372 5566 5451.",
  },
  en: {
    sentTitle: "Your request has been sent",
    sentBody: "We will reply within 24 working hours. For urgent matters call +372 5566 5451.",
    failedTitle: "Sending failed",
    failedBody: "Please fill the form or call +372 5566 5451.",
  },
};

function InquirySentCard({ lang, idx }) {
  const c = SYSTEM_COPY[lang] || SYSTEM_COPY.et;
  return (
    <div className="flex justify-start" data-testid={`ai-inquiry-sent-${idx}`}>
      <div className="max-w-[90%] rounded-2xl border border-teal-300/70 bg-teal-200/20 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <div className="h-7 w-7 rounded-full bg-teal-500 text-white flex items-center justify-center shrink-0 text-[14px] font-bold">✓</div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-teal-700">{c.sentTitle}</div>
            <div className="text-[12px] text-surface-inkSoft mt-1 leading-relaxed">{c.sentBody}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InquiryFailedCard({ lang, idx }) {
  const c = SYSTEM_COPY[lang] || SYSTEM_COPY.et;
  return (
    <div className="flex justify-start" data-testid={`ai-inquiry-failed-${idx}`}>
      <div className="max-w-[90%] rounded-2xl border border-amber-300/70 bg-amber-50 px-4 py-3">
        <div className="flex items-start gap-2.5">
          <div className="h-7 w-7 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0 text-[14px] font-bold">!</div>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-amber-700">{c.failedTitle}</div>
            <div className="text-[12px] text-surface-inkSoft mt-1 leading-relaxed">{c.failedBody}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ m, idx, busy, thinkingLabel }) {
  const isUser = m.role === "user";
  const content = m.content
    ? (isUser ? m.content : sanitizeAIText(m.content))
    : (busy && !isUser
        ? (
          <span className="inline-flex items-center gap-2 text-surface-inkSoft">
            <Loader2 className="h-4 w-4 animate-spin" /> {thinkingLabel}
          </span>
        )
        : "");
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-sapphire-700 text-white rounded-tr-sm"
            : "bg-white border border-surface-outline/60 text-surface-ink rounded-tl-sm shadow-ambient"
        }`}
        data-testid={`ai-msg-${m.role}-${idx}`}
      >
        {content}
      </div>
    </div>
  );
}

/**
 * Renders the scrollable list of chat messages, including two kinds of
 * "system" cards that the AI Assistant emits when it dispatches an inquiry.
 * Purely presentational — takes a scrollRef from the parent so the parent
 * can drive auto-scroll on new content.
 */
export default function MessageList({ messages, busy, lang, thinkingLabel, scrollRef }) {
  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-4 py-5 space-y-3"
      data-testid="ai-messages"
    >
      {messages.map((m, i) => {
        if (m.role === "system" && m.kind === "inquiry_sent") return <InquirySentCard key={i} lang={lang} idx={i} />;
        if (m.role === "system" && m.kind === "inquiry_failed") return <InquiryFailedCard key={i} lang={lang} idx={i} />;
        return <ChatBubble key={i} m={m} idx={i} busy={busy} thinkingLabel={thinkingLabel} />;
      })}
    </div>
  );
}
